/**
 * GeoRef — the one georeferencing transform every imported product carries.
 * Browser mirror of backend/geometry/georef.py (same JSON, same maths) so the
 * "define coordinates" dialog can preview instantly. Parity cases are shared in
 * ./__fixtures__/georef-parity.json (checked by Jest and pytest).
 *
 * Local frame (metres): x along the line, y across it, z elevation.
 *   E = a*x + b*y + tx     N = c*x + d*y + ty     Z = z + elevation_offset
 * The world CRS is projected; lon/lat input is moved to its UTM zone.
 * Design: docs/GEOREFERENCING_AND_MODEL_IMPORT.md
 */
import proj4 from 'proj4';
import { computeAffineTransform } from './georeference';

export const GEOREF_VERSION = 1;
export const SPACING_TOLERANCE_PCT = 2;
export const LOCAL_CRS = 'local';
export const MODES = ['local', 'file', 'tie_points', 'origin_azimuth', 'gcps'];
const IDENTITY = { a: 1, b: 0, tx: 0, c: 0, d: 1, ty: 0 };

// ── CRS registry (bundled short list; UTM zones are generated on demand) ────

const BUNDLED = [
  { code: 'EPSG:4326', label: 'WGS 84 (lon/lat)', def: null },
  { code: 'EPSG:3857', label: 'WGS 84 / Web Mercator', def: null },
  { code: 'EPSG:2277', label: 'NAD83 / Texas Central (ftUS)', def: '+proj=lcc +lat_0=29.6666666666667 +lon_0=-100.333333333333 +lat_1=31.8833333333333 +lat_2=30.1166666666667 +x_0=699999.998983998 +y_0=3000000 +datum=NAD83 +units=us-ft +no_defs' },
  { code: 'EPSG:32140', label: 'NAD83 / Texas Central (m)', def: '+proj=lcc +lat_0=29.6666666666667 +lon_0=-100.333333333333 +lat_1=31.8833333333333 +lat_2=30.1166666666667 +x_0=700000 +y_0=3000000 +datum=NAD83 +units=m +no_defs' },
  { code: 'EPSG:2236', label: 'NAD83 / Florida East (ftUS)', def: '+proj=tmerc +lat_0=24.3333333333333 +lon_0=-81 +k=0.999941177 +x_0=200000.0001016 +y_0=0 +datum=NAD83 +units=us-ft +no_defs' },
  { code: 'EPSG:27700', label: 'OSGB36 / British National Grid', def: '+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 +x_0=400000 +y_0=-100000 +ellps=airy +towgs84=446.448,-125.157,542.06,0.15,0.247,0.842,-20.489 +units=m +no_defs' },
];

function utmDef(code) {
  const m = /^EPSG:(326|327|269)(\d{2})$/.exec(code);
  if (!m) return null;
  const zone = parseInt(m[2], 10);
  if (zone < 1 || zone > 60) return null;
  if (m[1] === '269') return { def: `+proj=utm +zone=${zone} +datum=NAD83 +units=m +no_defs`, label: `NAD83 / UTM zone ${zone}N` };
  const south = m[1] === '327';
  return { def: `+proj=utm +zone=${zone}${south ? ' +south' : ''} +datum=WGS84 +units=m +no_defs`, label: `WGS 84 / UTM zone ${zone}${south ? 'S' : 'N'}` };
}

export function normalizeCrs(crs) {
  const s = String(crs ?? '').trim();
  if (!s || s.toLowerCase() === LOCAL_CRS) return LOCAL_CRS;
  if (/^\d+$/.test(s)) return `EPSG:${s}`;
  if (/^epsg:/i.test(s)) return `EPSG:${s.split(':')[1].trim()}`;
  if (['WGS84', 'WGS 84', 'CRS84', 'OGC:CRS84'].includes(s.toUpperCase())) return 'EPSG:4326';
  return s;
}

/** Make sure proj4 knows `crs`; returns false when it cannot be defined in the browser. */
export function ensureCrs(crs) {
  const code = normalizeCrs(crs);
  if (code === LOCAL_CRS) return false;
  if (proj4.defs(code)) return true;
  const b = BUNDLED.find(x => x.code === code);
  const u = utmDef(code);
  const def = b?.def || u?.def;
  if (!def) return false;
  proj4.defs(code, def);
  return true;
}

// Metres per CRS unit as pyproj reports it (GET /api/crs/{code}); beats the proj4 definition.
const SERVER_UNITS = {};

/**
 * Register a CRS the browser does not bundle from the GET /api/crs/{code} answer
 * ({ code, proj4, metres_per_unit }), so the dialog can preview the transform locally.
 * The server definition replaces any bundled one (pyproj is authoritative); WGS 84 and
 * Web Mercator stay with proj4's built-ins. Returns false (and registers nothing) when
 * the answer carries no proj string or proj4js cannot build a projection from it; the
 * server keeps validating on save either way.
 */
export function registerServerCrs(info) {
  if (!info || typeof info.proj4 !== 'string' || !info.proj4.trim()) return false;
  const code = normalizeCrs(info.code);
  if (code === LOCAL_CRS || code === 'EPSG:4326' || code === 'EPSG:3857') return false;
  const previous = proj4.defs(code);
  try {
    proj4.defs(code, info.proj4);
    proj4(code, 'EPSG:4326').forward([0, 0]); // builds the projection; throws if unsupported
  } catch {
    if (previous) proj4.defs(code, previous); else delete proj4.defs[code];
    return false;
  }
  if (Number.isFinite(info.metres_per_unit) && info.metres_per_unit > 0) SERVER_UNITS[code] = info.metres_per_unit;
  return true;
}

export function crsLabel(crs) {
  const code = normalizeCrs(crs);
  return BUNDLED.find(x => x.code === code)?.label || utmDef(code)?.label || code;
}

/** CRS picker search over the bundled list + generated UTM zones + free EPSG text. */
export function searchCrs(query = '') {
  const q = query.trim().toLowerCase();
  const utm = [];
  // "utm 21", "utm21s", or just the zone ("21", "21n", "21 s"): users type only the zone.
  const m = /utm\s*(\d{1,2})\s*([ns])?/i.exec(query) || /^\s*(\d{1,2})\s*([ns])?\s*$/i.exec(query);
  if (m) {
    const z = parseInt(m[1], 10);
    if (z >= 1 && z <= 60) {
      const hemis = m[2] ? [m[2].toUpperCase()] : ['N', 'S'];
      hemis.forEach(h => {
        const code = `EPSG:${(h === 'N' ? 32600 : 32700) + z}`;
        utm.push({ code, label: utmDef(code).label });
      });
      if (!m[2] || m[2].toUpperCase() === 'N') utm.push({ code: `EPSG:${26900 + z}`, label: utmDef(`EPSG:${26900 + z}`).label });
    }
  }
  const list = [...utm, ...BUNDLED.map(({ code, label }) => ({ code, label }))]
    .filter(x => !q || utm.includes(x) || x.code.toLowerCase().includes(q) || x.label.toLowerCase().includes(q));
  const free = normalizeCrs(query);
  if (/^EPSG:\d{4,6}$/.test(free) && !list.some(x => x.code === free)) {
    list.push({ code: free, label: utmDef(free)?.label || `${free} (validated on save)` });
  }
  return list;
}

export function crsInfo(crs) {
  const code = normalizeCrs(crs);
  if (code === LOCAL_CRS) return { crs: code, projected: false, geographic: false, metresPerUnit: 1, known: true };
  if (!ensureCrs(code)) return { crs: code, projected: true, geographic: false, metresPerUnit: 1, known: false };
  const def = proj4.defs(code);
  const geographic = def.projName === 'longlat';
  const metresPerUnit = SERVER_UNITS[code] || def.to_meter || (def.units === 'us-ft' ? 1200 / 3937 : def.units === 'ft' ? 0.3048 : 1);
  return { crs: code, projected: !geographic, geographic, metresPerUnit, known: true };
}

/**
 * Unit label ('m', 'ftUS', 'ft', 'deg') and name for a CRS. `server` is the optional
 * GET /api/crs/{code} answer (pyproj, authoritative); without it the bundled proj4
 * definitions decide, so a feet-based CRS outside the bundled list is only labelled
 * correctly when the lookup succeeded.
 */
export function crsUnits(crs, server = null) {
  if (server?.unit_label) {
    return { label: server.unit_label, metresPerUnit: server.metres_per_unit ?? 1, name: server.name || crsLabel(crs), fromServer: true };
  }
  const info = crsInfo(crs);
  const def = info.known && info.projected ? proj4.defs(info.crs) : null;
  const label = info.geographic ? 'deg' : def?.units === 'us-ft' ? 'ftUS' : (def?.units === 'ft' || info.metresPerUnit === 0.3048) ? 'ft' : 'm';
  return { label, metresPerUnit: info.metresPerUnit, name: crsLabel(crs), fromServer: false };
}

export function utmCrsFor(lon, lat) {
  const zone = (Math.floor((lon + 180) / 6) % 60) + 1;
  return `EPSG:${(lat >= 0 ? 32600 : 32700) + zone}`;
}

export function toWgs84(x, y, crs) {
  const code = normalizeCrs(crs);
  if (code === LOCAL_CRS) throw new Error('A local-frame product has no world position; set a georeference first');
  if (code === 'EPSG:4326') return [x, y];
  if (!ensureCrs(code)) throw new Error(`CRS ${code} is not available in the browser`);
  return proj4(code, 'EPSG:4326', [x, y]);
}

export function fromWgs84(lon, lat, crs) {
  const code = normalizeCrs(crs);
  if (code === 'EPSG:4326') return [lon, lat];
  if (!ensureCrs(code)) throw new Error(`CRS ${code} is not available in the browser`);
  return proj4('EPSG:4326', code, [lon, lat]);
}

export function guessCrs(xs, ys) {
  if (!xs.length) return { kind: 'local', crs: LOCAL_CRS };
  const xmin = Math.min(...xs), xmax = Math.max(...xs), ymin = Math.min(...ys), ymax = Math.max(...ys);
  if (xmin >= -180 && xmax <= 180 && ymin >= -90 && ymax <= 90 && xmax - xmin < 1 && ymax - ymin < 1 && (Math.abs(xmin) > 1 || Math.abs(ymin) > 1)) {
    return { kind: 'geographic', crs: 'EPSG:4326' };
  }
  if (xmin >= 100000 && xmax <= 900000 && ymin >= 0 && ymax <= 10000000 && ymax > 10000) return { kind: 'utm-like', crs: null };
  if (Math.max(Math.abs(xmin), Math.abs(xmax), Math.abs(ymin), Math.abs(ymax)) > 10000) return { kind: 'projected', crs: null };
  return { kind: 'local', crs: LOCAL_CRS };
}

// ── Transform maths ─────────────────────────────────────────────────────────

export function invertAffine(t) {
  const det = t.a * t.d - t.b * t.c;
  if (Math.abs(det) < 1e-15) throw new Error('Singular georeference transform');
  const ia = t.d / det, ib = -t.b / det, ic = -t.c / det, id = t.a / det;
  return { a: ia, b: ib, tx: -(ia * t.tx + ib * t.ty), c: ic, d: id, ty: -(ic * t.tx + id * t.ty) };
}

export function similarity(scale, rotationRad, originLocal, originWorld) {
  const a = scale * Math.cos(rotationRad);
  const c = scale * Math.sin(rotationRad);
  const b = -c, d = a;
  const [x0, y0] = originLocal;
  return { a, b, tx: originWorld[0] - (a * x0 + b * y0), c, d, ty: originWorld[1] - (c * x0 + d * y0) };
}

/** Least-squares affine from ≥3 GCPs — computeAffineTransform (lib/georeference.js) on centred data. */
export function fitAffineLstsq(localPts, worldPts) {
  if (localPts.length < 3 || localPts.length !== worldPts.length) throw new Error('A GCP fit needs at least 3 tie points');
  const n = localPts.length;
  const sc = [0, 1].map(k => localPts.reduce((s, p) => s + p[k], 0) / n);
  const dc = [0, 1].map(k => worldPts.reduce((s, p) => s + p[k], 0) / n);
  const t = computeAffineTransform(localPts.map((p, i) => ({
    px: p[0] - sc[0], py: p[1] - sc[1], lon: worldPts[i][0] - dc[0], lat: worldPts[i][1] - dc[1],
  })));
  if (!t) throw new Error('Tie points are collinear; a GCP fit needs points that span an area');
  return {
    a: t.a, b: t.b, tx: dc[0] + t.c - (t.a * sc[0] + t.b * sc[1]),
    c: t.d, d: t.e, ty: dc[1] + t.f - (t.d * sc[0] + t.e * sc[1]),
  };
}

const round = (v, n) => Math.round(v * 10 ** n) / 10 ** n;

export function transformMetrics(t) {
  const sx = Math.hypot(t.a, t.c), sy = Math.hypot(t.b, t.d);
  const rot = (Math.atan2(t.c, t.a) * 180) / Math.PI;
  return { scale: round((sx + sy) / 2, 9), rotation_deg: round(rot, 6), azimuth_deg: round((((90 - rot) % 360) + 360) % 360, 6) };
}

export function lineSpacing(xs) {
  const vals = [...new Set(xs.map(v => Math.round(v * 1e6) / 1e6))].sort((a, b) => a - b);
  const gaps = vals.slice(1).map((v, i) => v - vals[i]).filter(g => g > 1e-9).sort((a, b) => a - b);
  if (!gaps.length) return null;
  const m = Math.floor(gaps.length / 2);
  return round(gaps.length % 2 ? gaps[m] : (gaps[m - 1] + gaps[m]) / 2, 6);
}

/**
 * Local start/end of the line for the "Start and end points" mode: the first/last electrode, or,
 * for models without electrodes (BERT triangles/zip, 1D JSON), the model x extent.
 * Returns { startX, endX, fromModelExtent }.
 */
export function lineEnds(electrodes = [], xRange = null) {
  const xs = electrodes.map(e => e.x).filter(Number.isFinite);
  if (xs.length) return { startX: Math.min(...xs), endX: Math.max(...xs), fromModelExtent: false };
  if (Array.isArray(xRange) && Number.isFinite(xRange[0]) && Number.isFinite(xRange[1])) {
    return { startX: xRange[0], endX: xRange[1], fromModelExtent: true };
  }
  return { startX: 0, endX: 0, fromModelExtent: false };
}

export function spacingCheck(fileLength, tieLength, nElectrodes = null, tolerancePct = SPACING_TOLERANCE_PCT) {
  const out = { file_length: fileLength, tie_length: tieLength, mismatch_pct: null, warning: null, tolerance_pct: tolerancePct };
  if (!fileLength || tieLength == null) return out;
  const pct = (Math.abs(tieLength - fileLength) / fileLength) * 100;
  out.mismatch_pct = round(pct, 3);
  if (nElectrodes && nElectrodes > 1) {
    out.file_spacing = round(fileLength / (nElectrodes - 1), 6);
    out.tie_spacing = round(tieLength / (nElectrodes - 1), 6);
  }
  if (pct > tolerancePct) {
    out.warning = `Tie points are ${tieLength.toFixed(2)} m apart but the file geometry spans ${fileLength.toFixed(2)} m `
      + `(${pct.toFixed(1)} % difference, limit ${tolerancePct} %). Check the coordinates or the electrode spacing.`;
  }
  return out;
}

// ── Builders ────────────────────────────────────────────────────────────────

function xy(p, local = false) {
  if (Array.isArray(p)) return [Number(p[0]), Number(p[1])];
  const pairs = local ? [['x', 'y'], ['E', 'N']] : [['E', 'N'], ['x', 'y'], ['e', 'n'], ['easting', 'northing'], ['lon', 'lat']];
  for (const [kx, ky] of pairs) {
    if (p && p[kx] !== undefined && p[ky] !== undefined && p[kx] !== '' && p[ky] !== '') return [Number(p[kx]), Number(p[ky])];
  }
  throw new Error('A point needs two coordinates');
}

function worldCrs(crs, pts) {
  const info = crsInfo(crs);
  if (info.crs === LOCAL_CRS || !info.geographic) return { crs: info.crs, pts, info };
  const lon = pts.reduce((s, p) => s + p[0], 0) / pts.length;
  const lat = pts.reduce((s, p) => s + p[1], 0) / pts.length;
  const utm = utmCrsFor(lon, lat);
  const ll = info.crs === 'EPSG:4326' ? pts : pts.map(p => toWgs84(p[0], p[1], info.crs));
  return { crs: utm, pts: ll.map(p => fromWgs84(p[0], p[1], utm)), info: crsInfo(utm) };
}

function makeGeoRef(crs, mode, transform, elevationOffset, inputs, derived) {
  return { version: GEOREF_VERSION, mode, crs, transform, elevation_offset: elevationOffset || 0, inputs, derived };
}

export function fromFile(crs, elevationOffset = 0) {
  const code = normalizeCrs(crs);
  if (code === LOCAL_CRS) throw new Error("'Coordinates in file' needs the CRS those coordinates are in");
  return makeGeoRef(code, 'file', { ...IDENTITY }, elevationOffset, {}, transformMetrics(IDENTITY));
}

export function fromTiePoints(startLocal, startWorld, endLocal, endWorld, crs,
  { fitScale = true, spacing = null, fileSpacing = null, nElectrodes = null, elevationOffset = 0 } = {}) {
  const sl = xy(startLocal, true), el = xy(endLocal, true);
  const w = worldCrs(crs, [xy(startWorld), xy(endWorld)]);
  if (w.crs === LOCAL_CRS) throw new Error('Pick the CRS the start and end coordinates are in');
  const [sw, ew] = w.pts;
  const mpu = w.info.metresPerUnit;
  const dl = [el[0] - sl[0], el[1] - sl[1]], dw = [ew[0] - sw[0], ew[1] - sw[1]];
  const lenL = Math.hypot(...dl), lenW = Math.hypot(...dw);
  if (lenL < 1e-9) throw new Error('Start and end electrodes are at the same local position');
  if (lenW < 1e-9) throw new Error('Start and end points are identical');
  const rot = Math.atan2(dw[1], dw[0]) - Math.atan2(dl[1], dl[0]);
  const ratio = spacing && fileSpacing ? spacing / fileSpacing : 1;
  const scale = fitScale ? lenW / lenL : ratio / mpu;
  const t = similarity(scale, rot, sl, sw);
  const check = spacingCheck(lenL * ratio, lenW * mpu, nElectrodes);
  if (!fitScale) {
    const land = [t.a * el[0] + t.b * el[1] + t.tx, t.c * el[0] + t.d * el[1] + t.ty];
    check.end_offset = round(Math.hypot(land[0] - ew[0], land[1] - ew[1]) * mpu, 4);
  }
  return makeGeoRef(w.crs, 'tie_points', t, elevationOffset,
    { start: { local: sl, world: sw }, end: { local: el, world: ew }, fit_scale: !!fitScale, spacing, file_spacing: fileSpacing, n_electrodes: nElectrodes },
    { ...transformMetrics(t), line_length: round(lenW * mpu, 4), spacing_check: check });
}

export function fromOriginAzimuth(originWorld, azimuthDeg, crs, originLocal = [0, 0], scale = 1, elevationOffset = 0) {
  const w = worldCrs(crs, [xy(originWorld)]);
  if (w.crs === LOCAL_CRS) throw new Error('Pick the CRS the origin is in');
  const az = Number(azimuthDeg);
  if (!Number.isFinite(az)) throw new Error('Azimuth must be a number of degrees');
  const ol = xy(originLocal, true);
  const t = similarity(scale / w.info.metresPerUnit, ((90 - az) * Math.PI) / 180, ol, w.pts[0]);
  return makeGeoRef(w.crs, 'origin_azimuth', t, elevationOffset,
    { origin: { local: ol, world: w.pts[0] }, azimuth_deg: az, scale }, transformMetrics(t));
}

export function fromGcps(gcps, crs, elevationOffset = 0) {
  const local = gcps.map(g => xy(g, true));
  const world0 = gcps.map(g => {
    if (g.E !== undefined && g.N !== undefined && g.E !== '' && g.N !== '') return [Number(g.E), Number(g.N)];
    if (g.lon !== undefined && g.lat !== undefined) return [Number(g.lon), Number(g.lat)];
    throw new Error(`Tie point ${g.id ?? ''} needs E/N`);
  });
  const w = worldCrs(crs, world0);
  if (w.crs === LOCAL_CRS) throw new Error('Pick the CRS the tie points are in');
  const t = fitAffineLstsq(local, w.pts);
  const mpu = w.info.metresPerUnit;
  const residuals = local.map(([x, y], i) => ({
    id: gcps[i].id ?? null,
    residual: round(Math.hypot(t.a * x + t.b * y + t.tx - w.pts[i][0], t.c * x + t.d * y + t.ty - w.pts[i][1]) * mpu, 6),
  }));
  const rms = Math.sqrt(residuals.reduce((s, r) => s + r.residual ** 2, 0) / residuals.length);
  return makeGeoRef(w.crs, 'gcps', t, elevationOffset,
    { gcps: gcps.map((g, i) => ({ id: g.id ?? null, x: local[i][0], y: local[i][1], E: w.pts[i][0], N: w.pts[i][1] })) },
    { ...transformMetrics(t), residuals, rms_residual: round(rms, 6) });
}

function sortedElectrodes(electrodes) {
  return [...(electrodes || [])].sort((p, q) => (p.x - q.x) || ((p.y || 0) - (q.y || 0)));
}

/** Same contract as backend build_georef(spec, electrodes). */
export function buildGeoRef(spec, electrodes = []) {
  const mode = spec.mode || 'local';
  if (!MODES.includes(mode)) throw new Error(`Unknown georef mode '${mode}'`);
  const crs = normalizeCrs(spec.crs);
  const z0 = Number(spec.elevation_offset) || 0;
  const elec = sortedElectrodes(electrodes);
  if (mode === 'local') return makeGeoRef(LOCAL_CRS, 'local', { ...IDENTITY }, z0, {}, {});
  if (mode === 'file') return fromFile(crs, z0);
  if (mode === 'tie_points') {
    if (!spec.start || !spec.end) throw new Error('Start and end points are required');
    const sl = spec.start_local || (elec.length ? [elec[0].x, elec[0].y || 0] : null);
    const el = spec.end_local || (elec.length ? [elec[elec.length - 1].x, elec[elec.length - 1].y || 0] : null);
    if (!sl || !el) throw new Error('start_local / end_local are required when the product has no electrodes');
    return fromTiePoints(sl, spec.start, el, spec.end, crs, {
      fitScale: spec.fit_scale !== false,
      spacing: spec.spacing ? Number(spec.spacing) : null,
      fileSpacing: elec.length ? lineSpacing(elec.map(e => e.x)) : null,
      nElectrodes: elec.length || spec.n_electrodes || null,
      elevationOffset: z0,
    });
  }
  if (mode === 'origin_azimuth') {
    if (!spec.origin || spec.azimuth_deg === undefined || spec.azimuth_deg === '') throw new Error('Origin point and azimuth are required');
    const ol = spec.origin_local || (elec.length ? [elec[0].x, elec[0].y || 0] : [0, 0]);
    return fromOriginAzimuth(spec.origin, Number(spec.azimuth_deg), crs, ol, Number(spec.scale) || 1, z0);
  }
  const gcps = (spec.gcps || []).map(g => {
    if (g.x === undefined && g.electrode !== undefined && g.electrode !== '') {
      const e = elec.find(q => String(q.id) === String(g.electrode));
      if (!e) throw new Error(`Electrode ${g.electrode} not found`);
      return { id: g.electrode, ...g, x: e.x, y: e.y || 0 };
    }
    return g;
  });
  return fromGcps(gcps, crs, z0);
}

// ── Applying a GeoRef ───────────────────────────────────────────────────────

export const isLocal = (g) => !g || normalizeCrs(g.crs) === LOCAL_CRS;

export function applyGeoRef(g, x, y = 0) {
  const t = g.transform;
  return [t.a * x + t.b * y + t.tx, t.c * x + t.d * y + t.ty];
}

export function georefToWgs84(g, x, y = 0) {
  const [e, n] = applyGeoRef(g, x, y);
  return toWgs84(e, n, g.crs);
}

export function georeferencePoints(points, g) {
  if (isLocal(g)) return points.map(p => ({ ...p }));
  return points.map(p => {
    const [E, N] = applyGeoRef(g, p.x, p.y || 0);
    const [lon, lat] = toWgs84(E, N, g.crs);
    return { ...p, E: round(E, 4), N: round(N, 4), lon: round(lon, 9), lat: round(lat, 9) };
  });
}

export function localFootprint(product) {
  const kind = product.geometryKind || product.geometry_kind || 'line';
  const elec = product.electrodes || [];
  const cells = product.cells || [];
  if (kind === 'grid' || kind === 'mesh3d') {
    const xs = [], ys = [];
    cells.forEach(c => { xs.push(c.x0 ?? c.x, c.x1 ?? c.x); ys.push(c.y0 ?? c.y ?? 0, c.y1 ?? c.y ?? 0); });
    elec.forEach(e => { xs.push(e.x); ys.push(e.y || 0); });
    if (!xs.length) return [];
    let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity;
    xs.forEach(v => { if (v < x0) x0 = v; if (v > x1) x1 = v; });
    ys.forEach(v => { if (v < y0) y0 = v; if (v > y1) y1 = v; });
    return [[x0, y0], [x1, y0], [x1, y1], [x0, y1], [x0, y0]];
  }
  const xs = elec.length ? elec.map(e => e.x) : cells.flatMap(c => [c.x0 ?? c.x, c.x1 ?? c.x]);
  if (!xs.length) return [];
  return [[Math.min(...xs), 0], [Math.max(...xs), 0]];
}

export function previewGeoJSON(electrodes, g, footprint = null) {
  const features = georeferencePoints(electrodes, g).map(e => ({
    type: 'Feature',
    properties: { kind: 'electrode', id: e.id, E: e.E, N: e.N, z: round((e.z || 0) + (g.elevation_offset || 0), 4) },
    geometry: { type: 'Point', coordinates: [e.lon, e.lat] },
  }));
  if (footprint && footprint.length >= 2) {
    const coords = footprint.map(([x, y]) => georefToWgs84(g, x, y).map(v => round(v, 9)));
    const closed = footprint.length >= 4 && footprint[0][0] === footprint[footprint.length - 1][0] && footprint[0][1] === footprint[footprint.length - 1][1];
    features.push({
      type: 'Feature', properties: { kind: 'footprint' },
      geometry: closed ? { type: 'Polygon', coordinates: [coords] } : { type: 'LineString', coordinates: coords },
    });
  }
  return { type: 'FeatureCollection', features, metadata: { crs: g.crs, mode: g.mode } };
}

/**
 * Georeference a client-side model entry (InversionPanel shape: { model, cells }).
 * Adds lon/lat to cells and electrodes and the legacy two-point `line` used by the
 * fence diagram. Mirrors backend geometry.products.apply_georef.
 */
export function applyGeoRefToModel(model, g) {
  const kind = model.geometryKind || 'line';
  const cells = georeferencePoints(model.cells || [], g);
  const electrodes = georeferencePoints(model.electrodes || [], g);
  let line = null;
  if (!isLocal(g) && (kind === 'line' || kind === 'mesh2d') && model.xRange) {
    line = [georefToWgs84(g, model.xRange[0], 0), georefToWgs84(g, model.xRange[1], 0)];
  }
  const fp = localFootprint(model);
  const footprint = !isLocal(g) && fp.length ? previewGeoJSON([], g, fp).features[0] : null;
  return { cells, electrodes, line, footprint, crs: isLocal(g) ? LOCAL_CRS : g.crs, georef: isLocal(g) ? null : g };
}
